import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Filter, ChevronDown } from 'lucide-react';

const SentimentByCategory = ({ data }) => {
  const [sortBy, setSortBy] = useState('total');
  const [showSortMenu, setShowSortMenu] = useState(false);
  
  // Calculate total for each category and sort data
  const formattedData = data.map(item => ({
    ...item,
    total: item.positive + item.negative + item.neutral
  })).sort((a, b) => {
    if (sortBy === 'total') return b.total - a.total;
    if (sortBy === 'positive') return b.positive - a.positive;
    if (sortBy === 'negative') return b.negative - a.negative;
    return b.total - a.total;
  });
  
  // Limit to top 7 categories for visibility
  const displayData = formattedData.slice(0, 7);
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((acc, entry) => acc + entry.value, 0);
      
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="text-sm font-medium">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center mt-1">
              <div
                className="w-2.5 h-2.5 rounded-full mr-1.5"
                style={{ backgroundColor: entry.color }}
              ></div>
              <span className="text-xs font-medium">{entry.name}:</span>
              <span className="text-xs ml-1">{entry.value}</span>
              <span className="text-xs text-gray-500 ml-1">
                ({Math.round((entry.value / total) * 100)}%)
              </span>
            </div>
          ))}
          <div className="text-xs font-medium mt-2 pt-1 border-t border-gray-200">
            Total: {total} mentions
          </div>
        </div>
      );
    }
    return null;
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-white p-5 rounded-xl shadow-sm border border-gray-100"
    >
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Sentiment by Category</h3>
          <p className="text-sm text-gray-500">Breakdown of sentiment distribution across categories</p>
        </div>
        <div className="relative">
          {/* <button 
            className="flex items-center space-x-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            onClick={() => setShowSortMenu(!showSortMenu)}
          >
            <Filter className="w-3.5 h-3.5 text-gray-500" />
            <span>Sort by: {sortBy === 'total' ? 'Total mentions' : sortBy === 'positive' ? 'Positive' : 'Negative'}</span>
            <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
          </button> */}
          
          {showSortMenu && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 z-10 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5"
            >
              <div className="py-1" role="menu" aria-orientation="vertical">
                <button
                  className={`block px-4 py-2 text-sm w-full text-left ${sortBy === 'total' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                  onClick={() => {
                    setSortBy('total');
                    setShowSortMenu(false);
                  }}
                >
                  Total mentions
                </button>
                <button
                  className={`block px-4 py-2 text-sm w-full text-left ${sortBy === 'positive' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                  onClick={() => {
                    setSortBy('positive');
                    setShowSortMenu(false);
                  }}
                >
                  Positive sentiment
                </button>
                <button
                  className={`block px-4 py-2 text-sm w-full text-left ${sortBy === 'negative' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}
                  onClick={() => {
                    setSortBy('negative');
                    setShowSortMenu(false);
                  }}
                >
                  Negative sentiment
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={displayData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            barGap={2}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={true} stroke="#d1d5db" strokeWidth={1} />
            <XAxis 
              dataKey="name" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
              iconType="circle"
              iconSize={8}
            />
            <Bar name="Positive" dataKey="positive" stackId="a" fill="#22c55e" radius={[4, 4, 0, 0]} />
            <Bar name="Neutral" dataKey="neutral" stackId="a" fill="#94a3b8" radius={[0, 0, 0, 0]} />
            <Bar name="Negative" dataKey="negative" stackId="a" fill="#ef4444" radius={[0, 0, 4, 4]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
        {displayData.map((item, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-medium mb-1.5">{item.name}</div>
            <div className="flex flex-col space-y-1.5">
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                <div className="text-xs">Positive: {item.positive}</div>
                <div className="ml-auto text-xs text-gray-500">
                  {Math.round((item.positive / item.total) * 100)}%
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-gray-400 mr-2"></div>
                <div className="text-xs">Neutral: {item.neutral}</div>
                <div className="ml-auto text-xs text-gray-500">
                  {Math.round((item.neutral / item.total) * 100)}%
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-red-500 mr-2"></div>
                <div className="text-xs">Negative: {item.negative}</div>
                <div className="ml-auto text-xs text-gray-500">
                  {Math.round((item.negative / item.total) * 100)}%
                </div>
              </div>
            </div>
          </div>
        ))}
      </div> */}
    </motion.div>
  );
};

export default SentimentByCategory;